import { EditorView } from "@tiptap/pm/view";

export type TextEditorContent = { html: string; text: string };

const escapeHtml = (text: string) => {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const escapeAttr = (text: string) => escapeHtml(text);

const stripTokenBraces = (token: string) =>
  token
    .replace(/^\s*\{\{/, "")
    .replace(/\}\}\s*$/, "")
    .trim();

const mentionSpan = (tokenWithBraces: string) => {
  const token = tokenWithBraces.trim();
  const visible = stripTokenBraces(token);
  return `<span data-type="mention" data-id="${escapeAttr(
    token
  )}" data-label="${escapeAttr(token)}">${escapeHtml(visible)}</span>`;
};

const replaceMentionSpansWithToken = (html: string) => {
  return html.replace(
    /<span\b([^>]*\bdata-type=(['"])mention\2[^>]*)>([\s\S]*?)<\/span>/gi,
    (full, attrs) => {
      const labelMatch = String(attrs).match(/data-label=(['"])(.*?)\1/i);
      const idMatch = String(attrs).match(/data-id=(['"])(.*?)\1/i);
      const token = (labelMatch?.[2] ?? idMatch?.[2] ?? "").trim();
      return token ? token : full;
    }
  );
};

// Convert editor HTML (which may include mention spans) into storage HTML
// where mentions are represented as raw `{{token}}` text.
export const convertMentionsToTokensInHtml = (html: string) => {
  return replaceMentionSpansWithToken(html || "");
};

const replaceTokensWithMentionSpansInHtml = (html: string) => {
  return html.replace(/\{\{[^}]+\}\}/g, (token) => mentionSpan(token));
};

const textToHtml = (text: string) => {
  // Avoid regex on uncontrolled input (security scanners may flag polynomial regex behavior).
  // Some payloads show up with trailing "\" from escaping; strip them in linear time.
  const raw = String(text ?? "");
  let end = raw.length;
  while (end > 0 && raw[end - 1] === "\\") {
    end -= 1;
  }
  const cleaned = raw.slice(0, end).trim();

  const parts: string[] = [];
  let lastIndex = 0;
  const re = /\{\{([^}]+)\}\}/g;
  let match: RegExpExecArray | null;

  while ((match = re.exec(cleaned)) !== null) {
    const before = cleaned.slice(lastIndex, match.index);
    if (before) parts.push(escapeHtml(before));

    const token = `{{${match[1]}}}`;
    parts.push(mentionSpan(token));

    lastIndex = match.index + match[0].length;
  }

  const after = cleaned.slice(lastIndex);
  if (after) parts.push(escapeHtml(after));

  // Keep any newlines coming from plain text (optional).
  return `<p>${parts.join("").replace(/\n/g, "<br />")}</p>`;
};

const parseHtmlToContent = (html: string): TextEditorContent => {
  // Normalize:
  // 1) mention spans -> {{token}}
  // 2) {{token}} -> mention spans with data-* attrs
  const normalizedHtml = replaceTokensWithMentionSpansInHtml(
    replaceMentionSpansWithToken(html)
  );

  return {
    html: normalizedHtml,
    text: "",
  };
};

const parseTextToContent = (text: string): TextEditorContent => {
  return {
    html: textToHtml(text),
    text: "",
  };
};

export const convertValueToTextEditorContent = (
  value: { html?: string; text?: string } | null | undefined
): TextEditorContent => {
  if (value?.html) {
    return parseHtmlToContent(value.html);
  } else if (value?.text) {
    return parseTextToContent(value.text);
  }

  return {
    html: "",
    text: "",
  };
};

export const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => resolve("");
    reader.readAsDataURL(file);
  });

export const insertImageIntoView = (view: EditorView, src: string): void => {
  const { state } = view;
  const imageType = state.schema.nodes.image;
  if (!imageType) {
    return;
  }

  const imageNode = imageType.create({ src });
  let tr = state.tr.replaceSelectionWith(imageNode).scrollIntoView();

  // Add a paragraph after the image so the cursor doesn't get "stuck" on the node.
  const paragraphType = state.schema?.nodes?.paragraph;
  if (paragraphType) {
    tr = tr.insert(tr.selection.to, paragraphType.create());
  }

  view.dispatch(tr);
};

// We have to have this function for cases we don't render the TextEditor component and need to access the text and not the html
export const convertHtmlToPlainText = (htmlString: string): string => {
  const html = String(htmlString ?? "").trim();
  if (!html) return "";

  // Browser / DOM environments
  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(html, "text/html");

    // Remove nodes that are irrelevant for text extraction
    // and that should never influence output
    doc
      .querySelectorAll("script, style, link, iframe, object, embed, noscript")
      .forEach((node) => node.remove());

    // Insert line breaks for elements that imply structure
    const blockLikeSelector =
      "br, p, div, li, section, article, header, footer, h1, h2, h3, h4, h5, h6, blockquote, pre";

    doc.querySelectorAll(blockLikeSelector).forEach((el) => {
      el.insertAdjacentText("beforeend", "\n");
    });

    const text = doc.body?.textContent ?? "";

    return text
      .replace(/\u00a0/g, " ")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/[ \t]{2,}/g, " ")
      .trim();
  }

  // Fallback: non-DOM environments (Node, edge cases)
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(
      /<\/(p|div|li|section|article|header|footer|h[1-6]|blockquote|pre)>/gi,
      "\n"
    )
    .replace(/<[^>]*>/g, " ")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
};
